// Canonical URL hashes for the hash-routed application shell. Keep these
// mappings free of React/browser dependencies so navigation surfaces and tests
// share one source of truth.
export const APP_TAB_HASH = Object.freeze({
  vandaag: '',
  agenda: 'agenda',
  inbox: 'inbox',
  // Chat (29 september 2026): 1:1 tussen collega's, direct onder Meldingen.
  chat: 'chat',
  taken: 'taken',
  timer: 'timer',
  processen: 'processen',
  dealbank: 'training',
  templates: 'templates',
  whiteboard: 'kpi',
  weekplan: 'weekplan',
  weekplanTeam: 'weekplan-team',
  maandranking: 'maandranking',
  coty: 'coty',
  alltime: 'alltime',
  calls: 'calls',
  archief: 'archief',
  applicants: 'aanmeldingen',
  requests: 'requests',
  candsearch: 'zoeken',
  markten: 'markten',
  werving: 'werving',
  apps: 'apps',
  designsystem: 'design',
  beheer: 'beheer-bi',
  // Match werklijsten delen de belkansenpagina.
  matchWorkflows: 'match-calls',
  newleads: 'match-leads',
  // Match (12 september 2026): de matchingpagina tussen vacatures en
  // kandidaten. Eigen tab, eigen hash; de diepe vormen (#match-job-<slug>,
  // #match-candidate-<slug>) leven in lib/nav-routes.mjs.
  match: 'match',
  // Console (voorheen Assistent) (15 september 2026) staat in beta: de rail-ingang verschijnt alleen
  // met de betavlag (config.assistent_beta_users, lib/assistent/gate.mjs).
  assistent: 'assistent',
});

// De CRM-werkruimtetabs: sleutel, volgorde, label en glyphsleutel op EEN plek
// (een feit, een bron, 18 sep 2026). Lezers: CrmBar en het CRM-uitklapmenu in
// de rail, de mobiele tabstrip en de Alles-zoekgroepen in CandidateSearch, de
// werkruimterijen van het zoekpalet, de zoekgroepen in useGlobalSearch, de
// Alles-filterknoppen (lib/crm/alles-feed.mjs), de handleiding, de padbalk
// (app/page.js) en de /ga-plekken (lib/console/plekken.mjs). `icon` is de
// EntityIcon-sleutel; Alles heeft er geen en tekent zijn klok via CrmTabIcon
// (components/HomeV2/CrmBar.js). Guard: lib/one-fact-one-source.test.mjs.
export const CRM_TABS = Object.freeze([
  { key: 'alles', label: 'Alles', icon: null },
  { key: 'process', label: 'Processen', icon: 'process' },
  { key: 'candidate', label: 'Kandidaten', icon: 'candidate' },
  { key: 'client', label: 'Klanten', icon: 'client' },
  { key: 'prospect', label: 'Prospects', icon: 'prospect' },
  { key: 'contact', label: 'Contacten', icon: 'contact' },
  { key: 'job', label: 'Jobs', icon: 'job' },
  { key: 'calllist', label: 'Bellijsten', icon: 'callList' },
  { key: 'requests', label: 'Requests', icon: 'interviewRequest' },
  { key: 'cvsends', label: 'CV Sends', icon: 'cvSend' },
].map((tab) => Object.freeze(tab)));
export const CRM_TAB_LABEL = Object.freeze(Object.fromEntries(CRM_TABS.map((tab) => [tab.key, tab.label])));
export const CRM_TAB_ICON = Object.freeze(Object.fromEntries(CRM_TABS.map((tab) => [tab.key, tab.icon])));

// Raillabels, LETTERLIJK gekopieerd uit app/page.js (waar ze
// oorspronkelijk stonden voor de route-metadata van U40). Gelezen door
// app/page.js (kruimelpad) en lib/console/plekken.mjs (de /ga plekkenlijst),
// zodat er een schrijver en meerdere lezers zijn in plaats van twee losse
// stringlijsten die uit de pas kunnen lopen.
// newleads en assistent zijn TOEGEVOEGD (stonden niet in app/page.js:426): de
// rail kent voor die twee tabs alleen een losse letterlijke tekst, t('New Leads')
// in components/HomeV2/LeftRail.js:825 en 'Alle resultaten' (voorheen 'Console') op :530. Zonder deze twee
// regels had /ga twee plekken zonder label. Dezelfde strings, nu de ene bron;
// LeftRail.js zelf blijft ongewijzigd (buiten deze pure-lib unit), dus de twee
// letterlijke teksten daar zijn nog niet naar hier verwezen.
export const APP_TAB_LABEL = Object.freeze({ vandaag: 'Vandaag', candsearch: 'CRM Werkruimte', processen: 'Processen', taken: 'Acties en taken', calls: 'Call logs', agenda: 'Agenda', inbox: 'Meldingen', chat: 'Chat', whiteboard: 'Targets', weekplan: 'Weekplan', weekplanTeam: 'Team weekplannen', dealbank: 'Training', beheer: 'Management', markten: 'Markten', werving: 'Werving', archief: 'Archief', requests: 'Requests', applicants: 'Sollicitaties', timer: 'Timer', templates: 'Templates', maandranking: 'Maandranking', coty: 'COTY', alltime: 'All time', apps: 'Apps', designsystem: 'Design System', matchWorkflows: 'Match', match: 'Match', newleads: 'Nieuwe leads', assistent: 'Alle resultaten', medewerker: 'Medewerker' });

// De pagina van EEN collega (management, 6 oktober 2026): #medewerker-<userId>,
// geopend vanuit de groep Collega's in de globale zoeker. Geen APP_TAB_HASH-sleutel,
// want de route bestaat alleen met een gebruikers-id; app/page.js leest hem hier.
export const MEDEWERKER_HASH_PREFIX = 'medewerker-';
export function medewerkerHash(userId) {
  const id = String(userId ?? '').trim();
  return id ? `#${MEDEWERKER_HASH_PREFIX}${encodeURIComponent(id)}` : '';
}
export function parseMedewerkerHash(hash) {
  const bare = String(hash || '').replace(/^\/?#/, '').split('?')[0];
  if (!bare.startsWith(MEDEWERKER_HASH_PREFIX)) return null;
  const raw = bare.slice(MEDEWERKER_HASH_PREFIX.length);
  if (!raw) return null;
  try { return decodeURIComponent(raw); } catch { return raw; }
}

// Each CRM workspace subpage needs its own URL. A generic #zoeken link still
// means "open the CRM where I left it"; these hashes select an exact subpage.
export const CRM_NAV_HASH = Object.freeze({
  alles: 'zoeken-alles',
  processen: 'processen',
  candidates: 'zoeken-kandidaten',
  clients: 'zoeken-klanten',
  jobs: 'zoeken-jobs',
  prospects: 'zoeken-prospects',
  contacts: 'zoeken-contacten',
  callLists: 'zoeken-bellijsten',
  interviewRequests: 'zoeken-interviewverzoeken',
  cvSends: 'zoeken-cv-sends',
});

export const CRM_HASH_TAB = Object.freeze({
  'zoeken-alles': 'alles',
  processen: 'process',
  'zoeken-kandidaten': 'candidate',
  'zoeken-klanten': 'client',
  'zoeken-jobs': 'job',
  'zoeken-prospects': 'prospect',
  'zoeken-contacten': 'contact',
  'zoeken-bellijsten': 'calllist',
  'zoeken-interviewverzoeken': 'requests',
  'zoeken-cv-sends': 'cvsends',
});

const CRM_TAB_HASH = Object.freeze(Object.fromEntries(
  Object.entries(CRM_HASH_TAB).map(([hash, tab]) => [tab, hash]),
));

export function appTabHref(tab) {
  const hash = APP_TAB_HASH[tab];
  return hash ? `/#${hash}` : '/';
}

export function crmNavHref(key) {
  const hash = CRM_NAV_HASH[key];
  return hash ? `/#${hash}` : '/#zoeken';
}

// Een CRM-subpagina mag sinds de URL-presets een query dragen
// (#zoeken-kandidaten?owner=7&c=...). Die hoort bij het filter, niet bij de
// route, dus hij gaat er af voordat we de tab opzoeken. Zonder dit viel elke
// preset-link door naar 'vandaag'.
export function crmTabFromHash(hash) {
  const bare = String(hash || '').replace(/^#/, '').split('?')[0];
  return CRM_HASH_TAB[bare] || null;
}

export function crmHashForTab(tab) {
  return CRM_TAB_HASH[tab] || null;
}

export function crmTabHref(tab) {
  const hash = crmHashForTab(tab);
  return hash ? `/#${hash}` : '/#zoeken';
}

// Terugkeren naar Processen vanuit een venster dat boven het procesraster met een
// URL-preset (#processen?<query>) opende: het raster IS dan Processen, met zijn
// filter. Geeft de preset-hash om naar terug te schrijven, of null als de gewone
// weg geldt (goProcessen, die mobiel de eigen Processen-pagina opent en op desktop
// de kale procestab). `presetRoute` is { hash, tab } van de actieve URL-preset.
export function processenReturnHash({ activeTab, presetRoute } = {}) {
  if (activeTab !== 'candsearch' || !presetRoute || presetRoute.tab !== 'process') return null;
  const hash = String(presetRoute.hash || '');
  return /^#processen\?./.test(hash) ? hash : null;
}
